// The brand configuration on the wire (spec/03 §12, spec/08 §4.16 and §4.20, spec/14 §14; F-ORG-04).
//
//   GET /v1/meta       unauthenticated   the brand clients render, the platform's feature flags, and the
//                                        ways in switched on: social providers, and phone sign-in
//   GET /v1/hq/brand   hq:settings       the brand as HQ edits it, with its revision as the ETag
//   PUT /v1/hq/brand   hq:settings       change HQ's values, If-Match; each changed group audited
//
// **`GET /v1/meta` is the only place a web client reads runtime brand values from** (F-ORG-04). It
// is fetched before anyone signs in — the kiosk's lock screen, player web's sign-up with the terms it
// must show — so it needs no session, and carries nothing that is not already public: what a surface
// paints, the legal documents it links, the hosts it links to. It is served `no-cache`, so a change
// HQ makes reaches each client on its next fetch.
//
// `flags` are the platform's values of every feature flag — what a client may dark-launch on before
// it knows a venue. A flag turned on for one organisation or venue reaches that venue's surfaces
// through its settings, not here. Public settings join this response, additively, with the first
// setting a client must read before it signs in; none exists yet.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { HqBrandViewSchema, PublicBrandSchema, BrandRuntimeChangeSchema } from "../brand/configuration.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { HQ_SETTINGS } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { SocialProviderSchema } from "./auth.js";
import { ConditionalWriteHeaderSchema } from "./venues.js";
export const MetaSchema = z
    .object({
    brand: PublicBrandSchema,
    /** Every registered feature flag, at platform scope: on or off for everyone. */
    flags: z.record(z.string(), z.boolean()),
    /**
     * Which ways in are on here, so a screen offers only one that works: the social providers this deployment has
     * switched on, Google, Discord and Steam (F-IAM-03 … F-IAM-05; infra/runbooks/social-sign-in.md), names only and
     * never a client id or a secret (a reader ignores a name it does not know); and
     * whether a phone and a one-time code sign anyone in, up or through an invitation (`auth.phoneSignIn`, the
     * Platform's switch, spec/07 §3.1; off since the owner's instruction of 2026-10-08).
     */
    signIn: z
        .object({
        social: z.array(SocialProviderSchema),
        /**
         * `auth.phoneSignIn`. `false`: every phone sign-in, sign-up and verification endpoint answers
         * `PHONE_SIGN_IN_OFF`, invitations go by email only, and a client shows no phone option.
         */
        phone: z.boolean(),
    })
        .openapi('MetaSignIn', { description: 'The ways in switched on here: social providers, and phone sign-in.' }),
})
    .openapi('Meta', { description: 'What every client reads first: the brand configuration and the platform’s feature flags.' });
export const getMetaRoute = createRoute({
    method: 'get',
    path: '/v1/meta',
    operationId: 'getMeta',
    tags: ['meta'],
    summary: 'The brand configuration and feature flags',
    description: 'Unauthenticated. The brand configuration clients render (spec/03 §12) — identity, visuals with the computed ' +
        'design tokens, hosts, apps, the legal documents in force and defaults — the platform’s feature flags, and the ' +
        'ways in that are on (`signIn`: the social providers configured, and whether phone sign-in is on). ' +
        'Served `no-cache`: a change HQ makes is in the next fetch.',
    responses: {
        200: {
            description: 'The brand and the flags.',
            headers: z.object({ 'Cache-Control': z.string() }),
            content: { [JSON_CONTENT_TYPE]: { schema: MetaSchema } },
        },
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
const hqBrandResponse = (description) => ({
    description,
    headers: z.object({ ETag: z.string().openapi({ description: 'The version to send back as If-Match.' }) }),
    content: { [JSON_CONTENT_TYPE]: { schema: HqBrandViewSchema } },
});
export const getHqBrandRoute = createRoute({
    method: 'get',
    path: '/v1/hq/brand',
    operationId: 'getHqBrand',
    tags: ['brand'],
    summary: 'Read the brand configuration, as HQ edits it',
    description: 'The brand values HQ has set (support contacts, legal documents, design tokens, the kiosk’s default media, ' +
        'the limits on organisation branding), the deployment’s value of each, the messaging identity, and the brand ' +
        'clients get. Requires `hq:settings`.',
    [PERMISSION_EXTENSION]: HQ_SETTINGS,
    responses: {
        200: hqBrandResponse('The brand configuration.'),
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const putHqBrandRoute = createRoute({
    method: 'put',
    path: '/v1/hq/brand',
    operationId: 'putHqBrand',
    tags: ['brand'],
    summary: 'Change the brand values HQ sets',
    description: 'Sets a group, or hands it back to the deployment with null; groups left out are unchanged. A change is a new ' +
        'revision, and each changed group is one audit row. A colour below WCAG AA on the chosen surface is refused ' +
        'with the pair named. Send the `ETag` you read as `If-Match`. Requires `hq:settings`.',
    [PERMISSION_EXTENSION]: HQ_SETTINGS,
    request: {
        headers: ConditionalWriteHeaderSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: BrandRuntimeChangeSchema } }, required: true },
    },
    responses: {
        200: hqBrandResponse('The brand configuration as it is now.'),
        400: problemResponse('The change is not a brand value the schema accepts, or a colour fails WCAG AA.', 'validation-failed'),
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        412: problemResponse('Someone changed the brand since you read it.', 'stale-write'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=brand.js.map